import React, { useState } from 'react';
import StepLayout from '../components/StepLayout';
import IntlPhoneInput, { isValidE164 } from '../components/IntlPhoneInput';
import useEnterKey from '../hooks/useEnterKey';
import { api } from '../api';
import { guessSellerCountry } from '../utils/guessCountry';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// iter-26: spec — Contact Details "must fit one mobile viewport, no scroll".
// Heading + 4 fields + Next + Trustpilot card occupy ~390×844 in total.
// Desktop sizing unchanged. Mobile compression handled via the
// .step-compact-mobile wrapper class + @media overrides in takeover.css.
export default function Step02ContactDetails({ step, totalSteps, sessionId, setSessionId, sessionData, updateData, onNext }) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Phone is captured here, BEFORE the explicit Country step (Step04). Priority:
  //   1. an already-known country (e.g. on resume),
  //   2. the country resolved from the seller's IP (most reliable "where are they"),
  //   3. the ?country=us|gb ad-landing hint,
  //   4. a locale/timezone guess,
  // so a US seller defaults to +1 instead of the historical hardcoded +44 (GB).
  const [phoneCountry] = useState(() => {
    if (sessionData?.country) return String(sessionData.country).toLowerCase();
    if (sessionData?.ipCountry) return String(sessionData.ipCountry).toLowerCase();
    if (sessionData?.countryHint) return String(sessionData.countryHint).toLowerCase();
    return guessSellerCountry();
  });

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'This field is required.';
    if (!form.lastName.trim()) errs.lastName = 'This field is required.';
    if (!form.email.trim()) errs.email = 'This field is required.';
    else if (!emailRegex.test(form.email)) errs.email = 'Please enter a valid email';
    if (!form.phone.trim()) errs.phone = 'This field is required.';
    else if (!isValidE164(form.phone)) errs.phone = 'Phone number is invalid.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setSubmitError('');
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      };
      await api.updateContact(sessionId, payload);
      updateData({
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
      });
      onNext();
    } catch (err) {
      setSubmitError(err.message || 'Failed to save contact details.');
    } finally {
      setLoading(false);
    }
  };

  useEnterKey(() => handleSubmit({ preventDefault: () => {} }), { enabled: !loading });

  return (
    <StepLayout title="Please fill the following *">
      <form
        onSubmit={handleSubmit}
        className="contact-form step-compact-mobile"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 24,
        }}
      >
        <div className="form-field">
          <label className="form-field__label">
            First Name <span className="form-field__req">*</span>
          </label>
          <input
            type="text"
            placeholder="John"
            value={form.firstName}
            onChange={(e) => update('firstName', e.target.value)}
            autoComplete="given-name"
            className="form-field__input"
          />
          {errors.firstName && <p className="form-field__error">{errors.firstName}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            Last Name <span className="form-field__req">*</span>
          </label>
          <input
            type="text"
            placeholder="Doe"
            value={form.lastName}
            onChange={(e) => update('lastName', e.target.value)}
            autoComplete="family-name"
            className="form-field__input"
          />
          {errors.lastName && <p className="form-field__error">{errors.lastName}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            Email <span className="form-field__req">*</span>
          </label>
          <input
            type="email"
            placeholder="john@example.com"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            autoComplete="email"
            className="form-field__input"
          />
          {errors.email && <p className="form-field__error">{errors.email}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            Phone Number <span className="form-field__req">*</span>
          </label>
          <IntlPhoneInput
            value={form.phone}
            onChange={(v) => update('phone', v)}
            country={phoneCountry}
            placeholder={phoneCountry === 'us' ? '(201) 555-0123' : '07123 456789'}
            invalid={Boolean(errors.phone)}
          />
          {errors.phone && <p className="form-field__error">{errors.phone}</p>}
        </div>

        {submitError && <p className="form-field__error">{submitError}</p>}

        <div className="cta-row">
          <button
            type="submit"
            disabled={loading}
            className="cta-row__btn primary-cta"
          >
            {loading ? 'Saving...' : 'Next'}
          </button>
          <span className="enter-hint">press <strong>Enter ↵</strong></span>
        </div>
      </form>
    </StepLayout>
  );
}
